<template>
  <div class="non-newtonian-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$τ = k(du/dy)ⁿ$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>瞬时粘度 η (Effective)</label>
            <span class="val">{{ viscosity.toFixed(2) }} <small>Pa·s</small></span>
          </div>
          <div class="hud-item orange">
            <label>当前物理相态</label>
            <span class="val" :style="{color: stateColor}">{{ stateLabel }}</span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240">
          <defs>
             <filter id="glow-impact"><feGaussianBlur stdDeviation="5" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <radialGradient id="fluid-grad-inner" cx="50%" cy="50%" r="50%">
                <stop offset="0%" :stop-color="fluidColor" stop-opacity="1" />
                <stop offset="100%" :stop-color="fluidColor" stop-opacity="0.2" />
             </radialGradient>
          </defs>

          <!-- 流体池底波 -->
          <path :d="fluidPath" :fill="`url(#fluid-grad-inner)`" stroke="rgba(255,255,255,0.2)" stroke-width="1.5" />
          
          <!-- 微观颗粒结构 (仅在硬化时明显) -->
          <g v-if="viscosity > 15" opacity="0.6">
             <g v-for="i in 40" :key="'p'+i" :transform="`translate(${100 + (i*17)%200}, ${155 + (i*13)%65})`">
                <circle r="1.5" fill="#fff" />
                <line v-if="i%2==0" x1="0" y1="0" x2="10" y2="5" stroke="#fff" stroke-width="0.5" />
             </g>
          </g>

          <!-- 冲击球 -->
          <g :transform="`translate(200, ${ballY})`">
             <circle r="20" fill="#334155" stroke="#94a3b8" stroke-width="3" :filter="impactIntensity > 2 ? 'url(#glow-impact)' : ''" />
             <path d="M -10,-5 L 10,-5 M -10,5 L 10,5" stroke="#fff" stroke-width="1" opacity="0.3" />
             <text y="40" text-anchor="middle" fill="#64748b" font-size="10" font-weight="bold">冲击载荷</text>
          </g>

          <!-- 冲击波特效 -->
          <circle v-if="impactIntensity > 1" cx="200" :cy="150" :r="25 * impactIntensity" fill="none" :stroke="stateColor" :stroke-width="5 / impactIntensity">
             <animate attributeName="opacity" values="1;0" dur="0.3s" repeatCount="1" />
          </circle>
        </svg>

        <div class="interaction-hint">调整剪切速率，观察流体在“软泥”与“防弹板”间的转换</div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-col gap-2">
              <div class="flex items-center gap-4">
                 <span class="label" style="width: 100px">施加剪切速率 (力)</span>
                 <el-slider v-model="shearRate" :min="1" :max="100" size="small" class="flex-1"></el-slider>
                 <span class="val-tag">{{ shearRate }} %</span>
              </div>
              <div class="flex items-center justify-between">
                 <span class="label">流体特性 (Rheology)</span>
                 <el-radio-group v-model="fluidType" size="mini">
                    <el-radio-button label="Ooblek">剪切增稠 (胀流性)</el-radio-button>
                    <el-radio-button label="Ketchup">剪切变稀 (假塑性)</el-radio-button>
                 </el-radio-group>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>核心揭示：</strong>非牛顿流体的粘度并非恒定。<strong>剪切增稠</strong>（如淀粉液）受力越快，由于颗粒受阻排列，阻力呈指数级增长，瞬间变硬；<strong>剪切变稀</strong>（如番茄酱、油漆）则受力越剧烈，内部交联结构被破坏，流动更顺畅。记住：<strong>用力越狠，反应越奇异</strong>。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NonNewtonianViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      shearRate: 10,
      fluidType: 'Ooblek',
      t: 0,
      ballY: 50,
      impactIntensity: 0
    }
  },
  computed: {
    viscosity() {
       if (this.fluidType === 'Ooblek') {
          // Viscosity increases with shear rate
          return 1 + Math.pow(this.shearRate/15, 2.2);
       } else {
          // Viscosity decreases with shear rate
          return 40 / (1 + this.shearRate/8);
       }
    },
    stateLabel() {
       if (this.viscosity > 20) return "固化 (Solid-like)";
       if (this.viscosity > 5) return "粘稠 (Viscous)";
       return "稀薄 (Fluid-like)";
    },
    stateColor() {
       if (this.viscosity > 20) return "#f43f5e";
       if (this.viscosity > 5) return "#fbbf24";
       return "#38bdf8";
    },
    fluidColor() {
       return this.fluidType === 'Ooblek' ? '#e2e8f0' : '#881337';
    },
    fluidPath() {
       let points = "M 0,160 ";
       const centerDamp = 1 / (this.viscosity * 0.5);
       for (let x = 0; x <= 400; x += 10) {
          const dist = Math.abs(x - 200);
          const ripple = Math.sin(x * 0.08 + this.t * 0.1) * (10 / (this.viscosity * 0.2));
          let y = 160 + ripple;
          if (dist < 40) y += (40 - dist) * 0.3 * centerDamp;
          points += `L ${x},${y} `;
       }
       points += "L 400,240 L 0,240 Z";
       return points;
    }
  },
  methods: {
    animate() {
       this.t += 0.5;
       
       // Impact cycle
       const cycle = (this.t * 0.04) % 2;
       if (cycle < 1) {
          // Fall phase
          this.ballY = 40 + cycle * 115;
          this.impactIntensity = 0;
       } else {
          // Impact phase - bounce back depends on viscosity
          const recoil = 1 - (cycle - 1);
          const maxDip = 155;
          const bounceFactor = Math.min(1.2, this.viscosity / 10);
          this.ballY = maxDip - (recoil * 80 * bounceFactor);
          if (cycle < 1.1) this.impactIntensity = this.shearRate / 20;
       }
       
       this.animFrame = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.animFrame); }
}
</script>

<style scoped lang="scss">
.non-newtonian-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #fefce8; color: #854d0e; border: 1px solid #fef08a; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; }
      &.cyan { border-left: 3px solid #06b6d4; }
      &.orange { border-left: 3px solid #f59e0b; }
    }
  }
}

.interaction-hint { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 10px; font-weight: bold; }

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-col { flex-direction: column; } .gap-2 { gap: 8px; } .justify-between { justify-content: space-between; } .items-center { align-items: center; }
.val-tag { background: #f1f5f9; color: #475569; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; width: 50px; text-align: right; }

.discovery-card-compact {
  background: #fefce8; padding: 10px 14px; border-radius: 10px; border: 1px solid #fef9c3; display: flex; align-items: center; gap: 10px;
  i { color: #eab308; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #854d0e; line-height: 1.5; strong { color: #713f12; } }
}
</style>
